// The Skill instrument (GDD §15.1-1 + §15.1-4b, M5): the six-segment ladder
// ring, study speed as % of full ("studying at 95%"), the Skill-4 morale wall
// telegraphed UPWARD (readiness + countdown, never a surprise refusal), the
// sabbatical-time gate, and the Degree Rusting decay warning.

import type { Params } from '@lifespan/engine';
import { eta, type Clock } from '../game/clock.ts';
import { skillModel, type Session } from '../game/driver.ts';
import { JOB_TITLES } from '../game/jobs.ts';
import { money } from '../game/format.ts';

export function SkillPanel({
  session,
  P,
  clock,
}: {
  session: Session;
  P: Params;
  clock: Clock;
}) {
  const sk = skillModel(session, P);
  const segs = Array.from({ length: 6 }, (_, i) => i + 1);
  const pct = sk.required ? Math.min(1, sk.progress / sk.required) : 0;
  // dead lives freeze study: no "left at this pace" countdown on a corpse
  const yearsLeft =
    sk.alive && sk.studying && sk.required && sk.speed > 0
      ? (sk.required - sk.progress) / sk.speed
      : null;

  return (
    <section className="panel skills" aria-label="Skills">
      <div className="panel-title">
        Skill ladder — {JOB_TITLES[sk.J] ?? `Tier ${sk.J}`}
      </div>

      <div className="skill-ring" role="img" aria-label={`Skill ${sk.K} of 6`}>
        {segs.map((s) => (
          <div
            key={s}
            className={`skill-seg ${
              s <= sk.K ? 'done' : s === sk.next && sk.studying ? 'active' : ''
            }`}
            title={`Skill ${s}${s <= sk.K ? ' — complete' : ''}`}
          >
            {s}
            {s === sk.next && sk.studying && (
              <div className="skill-seg-fill" style={{ width: `${pct * 100}%` }} />
            )}
          </div>
        ))}
      </div>

      {sk.next === null ? (
        <div className="skill-status">Ladder complete — Master tier.</div>
      ) : sk.studying ? (
        <div className="skill-status">
          Studying Skill {sk.next} at{' '}
          <strong>{Math.round(sk.speed * 100)}% speed</strong> ·{' '}
          {sk.progress.toFixed(2)}/{sk.required} study-years
          {yearsLeft !== null && <> · {eta(yearsLeft, clock)} left at this pace</>}
          {sk.speed === 0 && <span className="warn"> — stalled (morale at the study floor)</span>}
        </div>
      ) : (
        <div className="skill-status">
          Next: Skill {sk.next} — tuition {money(sk.tuition!)}, {sk.required} study-years.{' '}
          {sk.canEnrollNow
            ? sk.enrolledSkill === sk.next && sk.progress > 0
              ? 'Resume at the annual review (tuition already paid).'
              : 'Enrol at the annual review.'
            : 'Gated — see below.'}
        </div>
      )}

      {sk.decayWarning && (
        <div className="skill-decay" role="alert">
          ⚠ Paused progress is rusting: {sk.progress.toFixed(2)} study-years decaying at
          25%/yr — below {P.progressEpsilon} the enrolment lapses and tuition is forfeit.
        </div>
      )}

      {sk.gates.m4 && (
        <div className={`skill-gate ${sk.gates.m4.ready ? 'ok' : 'blocked'}`}>
          Skill 4 wall: morale ≥ {sk.gates.m4.required} at enrolment —{' '}
          {sk.gates.m4.ready
            ? `ready (M ${sk.gates.m4.current.toFixed(0)})`
            : sk.gates.m4.neverOnThisPlan
              ? `this plan never reaches it (M settles at ${sk.gates.m4.mStar.toFixed(0)} → raise Living or rest)`
              : sk.gates.m4.countdown !== null
                ? `M ${sk.gates.m4.current.toFixed(0)} → ready in ~${eta(sk.gates.m4.countdown, clock)}`
                : `M ${sk.gates.m4.current.toFixed(0)}`}
        </div>
      )}

      {sk.gates.sab && (
        <div className={`skill-gate ${sk.gates.sab.ready ? 'ok' : 'blocked'}`}>
          Sabbatical time since last skill: {sk.gates.sab.haveYears.toFixed(2)} /{' '}
          {sk.gates.sab.requiredYears} years{' '}
          {sk.gates.sab.ready ? '— ready' : '— take Sabbatical mode to accrue (a month off is not a sabbatical)'}
        </div>
      )}

      {sk.gates.broke && sk.next !== null && (
        <div className="skill-gate blocked">
          BROKE — tuition is Living spend, locked until the balance recovers.
        </div>
      )}
    </section>
  );
}
